import React, { useState, useMemo, useEffect } from "react";
import { Stack, Group, Text, ActionIcon, Paper } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPhotoHeart,
  IconTools,
  IconPhoneCall,
  IconInfoCircle,
  IconLink,
} from "@tabler/icons-react";
import { FieldSchema, ValidationDict } from "../../../types/staticWebGenerator";
import {
  getRequiredTabsFromSchema,
  isFieldMissing,
  parseSchemaFields,
} from "../utils/validation";
import { getMissingFieldsForSection } from "../utils/fieldMapping";
import WebsiteGeneratorRequiredSectionsOptions from "./WebsiteGeneratorRequiredSectionsOptions";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BACKGROUND_WHITE = "#ffffff";

// Mapping dictionary: Schema section ID → Component section type
const SCHEMA_TO_SECTION_MAP: Record<string, SectionType> = {
  hero_section: "hero",
  our_services: "services",
  // Add more mappings as schema sections are added
};

// Standard required sections that always show
const STANDARD_REQUIRED_SECTIONS = [
  {
    id: "hero",
    schemaId: "hero_section",
    title: "Hero",
    subtitle: "Main banner and call to action",
    icon: <IconPhotoHeart size={20} />,
  },
  {
    id: "services",
    schemaId: "our_services",
    title: "Services",
    subtitle: "Showcase your offerings",
    icon: <IconTools size={20} />,
  },
  {
    id: "contact",
    schemaId: "find_us",
    title: "Contact",
    subtitle: "Provide contact information",
    icon: <IconPhoneCall size={20} />,
  },
  {
    id: "footer",
    schemaId: null,
    title: "Footer",
    subtitle: "Copyright and additional links",
    icon: <IconInfoCircle size={20} />,
  },
  {
    id: "bookingLink",
    schemaId: "booking_link",
    title: "Booking Link",
    subtitle: "Customize booking link copy and URL",
    icon: <IconLink size={20} />,
  },
];
import WebsiteGeneratorHeroContent from "./WebsiteGeneratorHeroContent";
import WebsiteGeneratorServicesContent from "./WebsiteGeneratorServicesContent";
import WebsiteGeneratorContactContent from "./WebsiteGeneratorContactContent";
import WebsiteGeneratorFooterContent from "./WebsiteGeneratorFooterContent";
import WebsiteGeneratorBookingLinkContent from "./WebsiteGeneratorBookingLinkContent";

interface HeroContent {
  title?: string;
  description?: string;
  body?: string;
  photos?: Array<{ url?: string; src?: string; filename?: string }>;
  includeBookingButton?: boolean;
  heroBookingCopy?: string;
}

interface ServicesContent {
  title?: string;
}

interface ContactContent {
  showDirections?: boolean;
  directions?: string;
  enableForm?: boolean;
  formEmail?: string;
  showMap?: boolean;
}

interface FooterContent {
  showSocialIcons?: boolean;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
}

interface BookingLinkContent {
  copy?: string;
  useCustomLink?: boolean;
  link?: string;
}

interface ImageData {
  id?: string | number;
  url?: string;
  src?: string;
  filename?: string;
  [key: string]: any;
}

interface WebsiteGeneratorRequiredSectionsContentProps {
  onBack: () => void;
  heroContent?: HeroContent;
  onHeroContentChange?: (content: HeroContent) => void;
  heroImageStateArray?: ImageData[];
  onHeroImageStateChange?: (images: ImageData[]) => void;
  selectedHeroPhoto?: ImageData | null;
  onHeroPhotoSelect?: (photo: ImageData) => void;
  servicesContent?: ServicesContent;
  onServicesContentChange?: (content: ServicesContent) => void;
  contactContent?: ContactContent;
  onContactContentChange?: (content: ContactContent) => void;
  footerContent?: FooterContent;
  onFooterContentChange?: (content: FooterContent) => void;
  bookingLinkContent?: BookingLinkContent;
  onBookingLinkContentChange?: (content: BookingLinkContent) => void;
  missingFields?: string[];
  fieldSchema?: FieldSchema | null;
  validationDict?: ValidationDict;
}

type SectionType =
  | "hero"
  | "services"
  | "contact"
  | "footer"
  | "bookingLink"
  | null;

const WebsiteGeneratorRequiredSectionsContent: React.FC<
  WebsiteGeneratorRequiredSectionsContentProps
> = ({
  onBack,
  heroContent,
  onHeroContentChange,
  heroImageStateArray,
  onHeroImageStateChange,
  selectedHeroPhoto,
  onHeroPhotoSelect,
  servicesContent,
  onServicesContentChange,
  contactContent,
  onContactContentChange,
  footerContent,
  onFooterContentChange,
  bookingLinkContent,
  onBookingLinkContentChange,
  missingFields = [],
  fieldSchema,
  validationDict = {},
}) => {
  const [selectedSection, setSelectedSection] = useState<SectionType>(null);

  const handleSectionSelect = (section: SectionType) => {
    setSelectedSection(section);
  };

  const handleBackToSections = () => {
    setSelectedSection(null);
  };

  // Get all required section IDs from schema
  const schemaRequiredSectionIds = useMemo(() => {
    if (!fieldSchema) return new Set<string>();
    const fields = parseSchemaFields(fieldSchema);
    const requiredIds = new Set<string>();
    fields.forEach((field) => {
      if (field.required) {
        const sectionId = field.path.split(".")[0];
        requiredIds.add(sectionId);
      }
    });
    return requiredIds;
  }, [fieldSchema]);

  // Schema sections to skip (they're in the Style tab)
  const SKIP_SCHEMA_SECTIONS = new Set(["color", "font"]);

  // Build the list of sections to show: standard + additional from schema
  const sectionsToShow = useMemo(() => {
    const sections = [...STANDARD_REQUIRED_SECTIONS];

    // Add any additional required sections from schema that aren't already in standard list
    if (fieldSchema) {
      const standardSchemaIds = new Set(
        STANDARD_REQUIRED_SECTIONS.map((s) => s.schemaId).filter(Boolean),
      );

      schemaRequiredSectionIds.forEach((schemaId) => {
        // Skip color and font sections (they're in Style tab)
        if (SKIP_SCHEMA_SECTIONS.has(schemaId)) {
          return;
        }

        // Only add if it's not already in standard sections
        if (
          !standardSchemaIds.has(schemaId) &&
          !SCHEMA_TO_SECTION_MAP[schemaId]
        ) {
          // This is a new section from schema that we don't have a component for yet
          // We'll add it with a generic handler
          sections.push({
            id: schemaId,
            schemaId: schemaId,
            title: schemaId
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase()),
            subtitle: "Schema section",
            icon: <IconInfoCircle size={20} />,
          });
        }
      });
    }

    return sections;
  }, [fieldSchema, schemaRequiredSectionIds]);

  // Check if a section has missing required fields
  // For required sections: check all required fields in the schema for that section
  const sectionHasMissingFields = (
    schemaId: string | null,
    sectionId: string,
  ): boolean => {
    if (!missingFields || missingFields.length === 0) {
      return false;
    }

    // If we have a schemaId and fieldSchema, check all required fields for that section
    if (schemaId && fieldSchema) {
      const fields = parseSchemaFields(fieldSchema);

      // Find all required fields that belong to this section (path starts with schemaId)
      const sectionRequiredFields = fields.filter((field) => {
        return field.required && field.path.startsWith(schemaId + ".");
      });

      // Check if any of these required fields are in the missingFields array
      const hasMissing = sectionRequiredFields.some((field) => {
        return missingFields.includes(field.path);
      });

      // Also check if the section itself is missing (top-level)
      const isSectionMissing = missingFields.includes(schemaId);

      return hasMissing || isSectionMissing;
    }

    // For sections without schemaId (like contact, footer, bookingLink),
    // use field path mappings to check if any fields in this section are missing
    const sectionMissingFields = getMissingFieldsForSection(
      sectionId,
      missingFields,
    );
    return sectionMissingFields.length > 0;
  };

  // Render specific section content
  if (selectedSection === "hero") {
    if (!onHeroContentChange || !onHeroPhotoSelect) return null;
    return (
      <WebsiteGeneratorHeroContent
        heroContent={heroContent}
        onContentChange={onHeroContentChange}
        heroImageStateArray={heroImageStateArray}
        onImageStateChange={onHeroImageStateChange}
        selectedHeroPhoto={selectedHeroPhoto}
        onPhotoSelect={onHeroPhotoSelect}
        onBack={handleBackToSections}
        bookingLinkContent={bookingLinkContent}
        onBookingLinkContentChange={onBookingLinkContentChange}
        missingFields={missingFields}
        requiredValidation={validationDict.hero || {}}
      />
    );
  }

  if (selectedSection === "services") {
    if (!onServicesContentChange) return null;
    const servicesValidation = validationDict.services || {};
    return (
      <WebsiteGeneratorServicesContent
        servicesContent={servicesContent}
        onContentChange={onServicesContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={servicesValidation}
      />
    );
  }

  if (selectedSection === "contact") {
    if (!onContactContentChange) return null;
    return (
      <WebsiteGeneratorContactContent
        contactContent={contactContent || {}}
        onContentChange={onContactContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={validationDict.contact || {}}
      />
    );
  }

  if (selectedSection === "footer") {
    if (!onFooterContentChange) return null;
    return (
      <WebsiteGeneratorFooterContent
        footerContent={footerContent || {}}
        onContentChange={onFooterContentChange}
        onBack={handleBackToSections}
        missingFields={missingFields}
        requiredValidation={validationDict.footer || {}}
      />
    );
  }

  if (selectedSection === "bookingLink") {
    if (!onBookingLinkContentChange) return null;
    return (
      <WebsiteGeneratorBookingLinkContent
        bookingLinkContent={bookingLinkContent}
        onContentChange={onBookingLinkContentChange}
        onBack={handleBackToSections}
      />
    );
  }

  // Render sections list: always show standard 6 + any additional from schema
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Required Sections
        </Text>
      </Group>

      <Stack gap="sm">
        {sectionsToShow.map((section) => {
          // Check if this section should be shown (if schema exists, only show if in schema)
          const shouldShow =
            !fieldSchema ||
            !section.schemaId ||
            schemaRequiredSectionIds.has(section.schemaId);
          if (!shouldShow) return null;

          const hasMissingFields = sectionHasMissingFields(
            section.schemaId,
            section.id,
          );
          const sectionType = section.id as SectionType;

          return (
            <Paper
              key={section.id}
              p="md"
              style={{
                border: `1px solid ${hasMissingFields ? "red" : COLOR_BORDER_LIGHT}`,
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => handleSectionSelect(sectionType)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = COLOR_BACKGROUND_LIGHT;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = COLOR_BACKGROUND_WHITE;
              }}
            >
              <Group justify="space-between" align="center">
                <Group gap="sm">
                  <ActionIcon
                    variant="light"
                    color="gray"
                    size="lg"
                    radius="md"
                  >
                    {section.icon}
                  </ActionIcon>
                  <Stack gap={0}>
                    <Text
                      fw={500}
                      size="sm"
                      c={hasMissingFields ? "red" : undefined}
                    >
                      {section.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {section.subtitle}
                    </Text>
                  </Stack>
                </Group>
                <ActionIcon variant="subtle" color="gray">
                  <IconChevronRight size={16} />
                </ActionIcon>
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default WebsiteGeneratorRequiredSectionsContent;
